import {useRef, useEffect, useState, useCallback} from "react";
import {delayedTrigger, MiniEvent} from "@cdx/common";
import {useTransition, animated} from "react-spring";
import {Box, css, cx, SimplePortal} from "@cdx/ds";
import {colorThemes, elementThemes} from "@cdx/ds/css/themes/themes.css";
import {cardStatusBaseTheme, cardStatusVariants} from "@cdx/ds/components/DSCard/DSCardTheme.css";

const messagesEvent = new MiniEvent();

let nextId = 1;

const valToMs = (val) => {
  if (typeof val !== "string") return 1500;
  return Math.max(1500, Math.min(10000, 1000 + val.length * 50));
};

const messenger = {
  /**
   *
   * @param {import("react").ReactNode} val
   * @param {{type?: "error" | "info", msToRemove?: number}} [options]
   * @returns
   */
  send: (val, {type, msToRemove = valToMs(val)} = {}) =>
    messagesEvent.emit(val, {type, msToRemove}),
};

const Message = ({frontMost, onRemove, message, msToRemove, style, id, type}) => {
  const removeMsRef = useRef(msToRemove);
  useEffect(() => {
    if (frontMost) {
      const trigger = delayedTrigger();
      trigger.fire(() => onRemove(id), removeMsRef.current);
      return () => trigger.cancel();
    }
  }, [frontMost, onRemove, id]);

  return (
    <animated.div
      style={style}
      onClick={() => onRemove(id)}
      data-cdx-clickable
      className={css(
        {
          display: "flex",
          px: "16px",
          py: "12px",
          bg: "true",
          elevation: 200,
          position: "absolute",
          bottom: 0,
          left: 0,
          width: "15rem",
          rounded: 4,
          minHeight: "commentField",
          overflowWrap: "break-word",
          color: "primary",
          textType: "prose14",
        },
        type === "error"
          ? cx(colorThemes.elementThemeBase, elementThemes.alert)
          : cx(cardStatusBaseTheme, cardStatusVariants.started)
      )}
    >
      {message}
    </animated.div>
  );
};

export const MessengerArea = () => {
  const [messages, setMessages] = useState([]);

  useEffect(() => {
    messagesEvent.addListener((message, {type, msToRemove}) =>
      setMessages((prev) => [...prev, {id: (nextId += 1), message, msToRemove, type}])
    );
  }, []);

  const handleRemove = useCallback((msgId) => {
    setMessages((prev) => prev.filter((msg) => msg.id !== msgId));
  }, []);

  const transition = useTransition(
    messages.map((message, i) => ({message, i: messages.length - i - 1})),
    {
      key: (item) => item.message.id,
      from: ({i}) => ({
        opacity: 0,
        translateY: `${1 + Math.min(i, 6) * 0.25}rem`,
        scale: 1 - Math.min(i, 6) * 0.02,
      }),
      enter: ({i}) => ({
        opacity: 1,
        translateY: `${Math.min(i, 6) * 0.25}rem`,
        scale: 1 - Math.min(i, 6) * 0.02,
      }),
      update: ({i}) => ({
        opacity: 1,
        translateY: `${Math.min(i, 6) * 0.25}rem`,
        scale: 1 - Math.min(i, 6) * 0.02,
      }),
      leave: ({i}) => ({
        opacity: 0.001,
        translateY: `${-0.5 + Math.min(i, 6) * 0.25}rem`,
        scale: 1,
      }),
    }
  );

  return (
    <SimplePortal>
      <Box position="fixed" style={{bottom: 24, left: 24, zIndex: 23}}>
        {transition((props, {message, i}) => (
          <Message
            id={message.id}
            message={message.message}
            frontMost={i === 0}
            onRemove={handleRemove}
            msToRemove={message.msToRemove}
            type={message.type}
            style={{
              pointerEvents: props.opacity.to((val) => (val > 0.9 ? "initial" : "none")),
              ...props,
            }}
          />
        ))}
      </Box>
    </SimplePortal>
  );
};

export default messenger;
